/**
 * Assignment Activity Modal - User Timeline Version
 * Simplified version without user-specific information
 */

import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  IconButton,
  Skeleton,
  Tabs,
  Tab,
} from '@mui/material'
import { Close } from '@mui/icons-material'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchSelectedUserAssignmentDetail,
  fetchLinkedServices,
  selectSelectedUserAssignmentDetails,
  selectSelectedUserAssignmentDetailsStatus,
  selectLinkedServicesStatus,
} from 'src/v3/manage/manage.slice'
import type { RosterAssignmentEventData } from '../../types'

// Tab Components
import TabPanel from './assignment-modal/tab-panel'
import OverviewTab from './assignment-modal/overview-tab'
import CoursesTab from './assignment-modal/courses-tab'
import ProgressMatrixTab from './assignment-modal/progress-matrix-tab'

dayjs.extend(relativeTime)

interface AssignmentActivityModalProps {
  event: RosterAssignmentEventData | null
  isOpen: boolean
  onClose: () => void
  linkedServiceID: string
  userID: string
}

export default function AssignmentActivityModal({
  event,
  isOpen,
  onClose,
  userID,
}: AssignmentActivityModalProps) {
  let dispatch = useAppDispatch()

  // Tab state
  let [activeTab, setActiveTab] = useState(0)

  // Redux selectors
  let assignment = useAppSelector(selectSelectedUserAssignmentDetails)
  let assignmentDetailStatus = useAppSelector(
    selectSelectedUserAssignmentDetailsStatus,
  )
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)

  // Fetch assignment details when modal opens
  useEffect(() => {
    if (
      isOpen &&
      event?.assignmentID &&
      userID &&
      (assignmentDetailStatus === 'uninitialized' ||
        assignment?.id !== event.assignmentID)
    ) {
      dispatch(
        fetchSelectedUserAssignmentDetail({
          userID,
          assignmentID: event.assignmentID,
        }),
      )
    }
  }, [
    isOpen,
    event?.assignmentID,
    userID,
    assignmentDetailStatus,
    assignment?.id,
    dispatch,
  ])

  // Fetch linked services for display
  useEffect(() => {
    if (isOpen && linkedServicesStatus === 'uninitialized') {
      dispatch(fetchLinkedServices())
    }
  }, [isOpen, linkedServicesStatus, dispatch])

  // Reset tab when modal closes
  useEffect(() => {
    if (!isOpen) {
      setActiveTab(0)
    }
  }, [isOpen])

  if (!event) {
    return null
  }

  // Derived loading state
  let isLoading =
    assignmentDetailStatus === 'pending' ||
    assignmentDetailStatus === 'uninitialized'

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      aria-labelledby="assignment-activity-modal-title"
      slotProps={{
        paper: {
          sx: {
            width: '80vw',
            height: '80vh',
            maxWidth: 'none',
            maxHeight: 'none',
            borderRadius: 2,
            bgcolor: 'background.default',
          },
        },
      }}
    >
      <DialogTitle>
        <Box className="flex items-center justify-between">
          <Box className="flex items-center gap-4">
            <Typography variant="h6" id="assignment-activity-modal-title">
              {assignment?.name || 'Assignment Activity'}
            </Typography>
            <Chip
              label={event.activityType
                .replace(/-/g, ' ')
                .replace(/\b\w/g, (l) => l.toUpperCase())}
              sx={{
                bgcolor: 'entityColors.assignment',
                color: 'entityColors.assignment',
                fontWeight: 500,
                '& .MuiChip-label': {
                  color: 'black',
                },
              }}
            />
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close dialog">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent className="p-4">
        <Box className="mb-4">
          <Typography variant="body2" color="text.secondary" className="mb-1">
            {dayjs(event.createdDate).format('MMMM D, YYYY [at] h:mm A')} •{' '}
            {dayjs(event.createdDate).fromNow()}
          </Typography>
          {event.description && (
            <Typography variant="body2">{event.description}</Typography>
          )}
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }} className="mb-4">
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
          >
            <Tab label="Overview" />
            <Tab label={`Courses (${assignment?.courses?.length || 0})`} />
            <Tab label="Progress Matrix" />
          </Tabs>
        </Box>

        {isLoading ? (
          <Box
            className="py-4"
            role="status"
            aria-live="polite"
            aria-label="Loading assignment details"
          >
            <Skeleton
              variant="rectangular"
              width="100%"
              height={300}
              className="rounded"
            />
          </Box>
        ) : assignment ? (
          <>
            <TabPanel value={activeTab} index={0}>
              <OverviewTab assignment={assignment} />
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              <CoursesTab assignment={assignment} />
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
              <ProgressMatrixTab assignment={assignment} />
            </TabPanel>
          </>
        ) : (
          <Box className="text-center py-8">
            <Typography variant="body1" color="text.secondary">
              Assignment details could not be loaded.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions className="p-4">
        <Button onClick={onClose} autoFocus>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
